import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { differenceInDays, differenceInWeeks, format } from 'date-fns';
import { cn } from '@/lib/utils';
import type {
  Game,
  ChallengeWithProgress,
  PlayerProgress,
  GameChallenge
} from '@/types/game';
import { useGameRegistry } from '@/hooks/useGameRegistry';
import { PremiumLock } from '@/components/PremiumLock';
import { getChallengeDisplayStyling, getChallengeAccessMessage } from '@/utils/challengeAccess';
import { getUserDateSafe } from '@/utils/userDate';
import { isContinuousGame, launchContinuousGame, GameLaunchError } from '@/services/gameLaunchService';
import { UnderConstructionTile } from '@/components/Games/UnderConstructionTile';

interface PlayerProgressStreamProps {
  gameId: string;
  game: Game;
  userId?: string; // Optional - if not provided, show non-auth view
  // IMA-88: Removed subscription prop - access control is now determined by backend
  // via challenge.accessControl (displayState, canPlay) from the API response
  onChallengeClick?: (challenge: GameChallenge, progress?: PlayerProgress) => void;
  onLockedClick?: (game: Game) => void; // Called when user clicks a locked challenge
  onArchivesClick?: () => void;
  onContinuousPlay?: (gameUrl: string, challengeId: number) => void;
  // D39: fires once today's challenge is known — Games.tsx uses it to enable card-zone navigation
  onTodayChallengeReady?: (challenge: ChallengeWithProgress) => void;
}

interface ProgressDisplay {
  challenge: GameChallenge & { displayLabel?: string };
  progress?: PlayerProgress;
  label: string;
  displayState: {
    starsEarned: number;
    completionText: string;
    activityState: 'not_played' | 'in_progress' | 'completed';
  };
}

// Date formatting function
function getChallengeDisplayLabel(publishDate: string, frequency: string, userDate: string): string {
  // Use the same date logic as challenge access for consistency
  const challengeDate = publishDate.split('T')[0]; // Extract YYYY-MM-DD
  
  if (frequency === 'daily') {
    // IMA-145: Parse as local midnight, NOT UTC. publishDate is a calendar date, not a UTC timestamp.
    const challengeDateObj = new Date(challengeDate + 'T00:00:00');

    if (challengeDate === userDate) {
      // TODAY format: "TODAY - DEC. 9" (US format with abbreviated month and day, uppercase)
      return `TODAY - ${format(challengeDateObj, 'MMM. d').toUpperCase()}`;
    }
    
    // Calculate yesterday for comparison
    const userDateObj = new Date(userDate + 'T00:00:00');
    const yesterdayObj = new Date(userDateObj.getTime() - 24 * 60 * 60 * 1000);
    const yesterdayStr = format(yesterdayObj, 'yyyy-MM-dd');
    
    if (challengeDate === yesterdayStr) return 'YESTERDAY';
    
    // For other dates, use the original challenge date formatting
    const today = new Date(userDate + 'T00:00:00');
    const daysDiff = differenceInDays(today, challengeDateObj);
    
    if (daysDiff <= 6) return format(challengeDateObj, 'EEE').toUpperCase(); // Day abbreviation
    return format(challengeDateObj, 'MMM d').toUpperCase();
  } else if (frequency === 'weekly') {
    // For weekly challenges, we need to handle the special case where the challenge date
    // might be in the future (for testing) or past
    const today = new Date(userDate + 'T00:00:00');
    // IMA-145: Parse as local midnight, NOT UTC. publishDate is a calendar date, not a UTC timestamp.
    const challengeDateObj = new Date(challengeDate + 'T00:00:00');
    const weeksDiff = differenceInWeeks(today, challengeDateObj); // Monday start
    
    // Handle both past and future weeks
    if (Math.abs(weeksDiff) === 0) return 'THIS WEEK';
    if (weeksDiff === 1) return 'LAST WEEK';
    if (weeksDiff === -1) return 'NEXT WEEK'; // Future week
    
    // For any other week, show the date range (Monday to Sunday)
    // Get the Monday of that week
    const monday = new Date(challengeDateObj);
    const dayOfWeek = monday.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(monday.getDate() + daysToMonday);
    
    // Get the Sunday of that week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    // Check if the week spans across two months
    const mondayMonth = format(monday, 'MMM');
    const sundayMonth = format(sunday, 'MMM');
    if (mondayMonth === sundayMonth) {
      return `${format(monday, 'MMM d')}-${format(sunday, 'd')}`.toUpperCase();
    } else {
      return `${format(monday, 'MMM d')}-${format(sunday, 'MMM d')}`.toUpperCase();
    }
  }
  // IMA-145: Parse as local midnight, NOT UTC. publishDate is a calendar date, not a UTC timestamp.
  const dateStr = publishDate.split('T')[0];
  return format(new Date(dateStr + 'T00:00:00'), 'MMM d').toUpperCase();
}

// Client-side challenge filtering
function getAvailableChallenges(challenges: ChallengeWithProgress[]): ChallengeWithProgress[] {
  const userLocalTime = new Date();

  const filtered = challenges.filter(challenge => {
    // IMA-145: Parse as local midnight, NOT UTC. publishDate is a calendar date, not a UTC timestamp.
    const dateStr = challenge.publishDate.split('T')[0];
    const publishDate = new Date(dateStr + 'T00:00:00');
    const isAvailable = publishDate <= userLocalTime;
    return isAvailable;
  });

  return filtered;
}

export function PlayerProgressStream({
  gameId,
  game,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userId,
  onChallengeClick,
  onLockedClick,
  onArchivesClick,
  onContinuousPlay,
  onTodayChallengeReady,
}: PlayerProgressStreamProps) {
  const { getChallengesForGame } = useGameRegistry();
  const [challenges, setChallenges] = useState<ChallengeWithProgress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Scroll overflow detection for mobile/tablet
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showRightIndicator, setShowRightIndicator] = useState(false);
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);

  // Check if scroll container has overflow in either direction
  useEffect(() => {
    const checkOverflow = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const hasOverflow = container.scrollWidth > container.clientWidth;
        // Check if there's content to the right (not scrolled to end)
        const canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth - 5;
        // Check if there's content to the left (scrolled away from start)
        const canScrollLeft = container.scrollLeft > 5;

        setShowRightIndicator(hasOverflow && canScrollRight);
        setShowLeftIndicator(hasOverflow && canScrollLeft);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkOverflow);
    }

    return () => {
      window.removeEventListener('resize', checkOverflow);
      if (container) {
        container.removeEventListener('scroll', checkOverflow);
      }
    };
  }, [challenges]);

  // Get user's local date for challenge access calculations
  const { userDate } = getUserDateSafe();

  // Check if this is a continuous game
  const isContinuous = isContinuousGame(game);

  // Handler for continuous game play button
  const handleContinuousPlay = useCallback(() => {
    if (!onContinuousPlay) {
      console.warn('[PlayerProgressStream] No onContinuousPlay handler provided');
      setError(new Error('Game launch not configured'));
      return;
    }

    try {
      const launchResult = launchContinuousGame(game);
      onContinuousPlay(launchResult.gameUrl, launchResult.challengeId);
    } catch (error) {
      console.error('[PlayerProgressStream] Failed to launch continuous game:', error);
      
      if (error instanceof GameLaunchError) {
        switch (error.errorType) {
          case 'CONFIGURATION':
            setError(new Error('Game configuration error. Please try again later.'));
            break;
          case 'NETWORK':
            setError(new Error('Network error. Please check your connection.'));
            break;
          case 'AUTHENTICATION':
            setError(new Error('Authentication failed. Please sign in again.'));
            break;
          default:
            setError(new Error('Unable to launch game. Please try again.'));
        }
      } else {
        setError(new Error('Unable to launch game. Please try again.'));
      }
    }
  }, [game, onContinuousPlay]);

  // Load challenges when component mounts or gameId changes (skip for continuous games)
  useEffect(() => {
    // Continuous games don't need challenges loaded
    if (isContinuous) {
      setLoading(false);
      setChallenges([]);
      setError(null);
      return;
    }

    const loadChallenges = async () => {
      setLoading(true);
      setError(null);

      try {
        const challengeData = await getChallengesForGame(gameId);
        setChallenges(challengeData);
      } catch (err) {
        console.error('[PlayerProgressStream] Error loading challenges:', err);
        setError(err instanceof Error ? err : new Error('Failed to load challenges'));
      } finally {
        setLoading(false);
      }
    };

    loadChallenges();
  }, [gameId, getChallengesForGame, isContinuous]);

  // Process challenges for display
  const displayItems = useMemo((): ProgressDisplay[] => {
    if (!challenges.length) return [];

    // Filter available challenges and get the 7 most recent for enhanced display
    const availableChallenges = getAvailableChallenges(challenges);
    const recentChallenges = availableChallenges
      .sort((a, b) => {
        // IMA-145: Parse as local midnight for consistency. publishDate is a calendar date.
        const aDate = new Date(a.publishDate.split('T')[0] + 'T00:00:00');
        const bDate = new Date(b.publishDate.split('T')[0] + 'T00:00:00');
        return bDate.getTime() - aDate.getTime();
      })
      .slice(0, 7);

    return recentChallenges.map((challengeWithProgress, index) => {
      const label = getChallengeDisplayLabel(challengeWithProgress.publishDate, game.challengeFrequency, userDate);

      // Extract progress data or use defaults
      const progress = challengeWithProgress.progress;
      const displayState = {
        starsEarned: progress?.starsEarned || 0,
        // in_progress checked first to prevent short-circuit fallthrough to ''
        // "In Progress" only on TODAY tile (index 0); archive tiles fall through to '---'
        completionText:
          progress?.activityState === 'in_progress'
            ? (index === 0 ? 'In Progress' : '---')
            : progress?.completionText || (progress?.activityState === 'not_played' || !progress ? '---' : ''),
        activityState: progress?.activityState || 'not_played' as const,
      };

      return {
        challenge: {
          ...challengeWithProgress,
          displayLabel: label,
        },
        progress,
        label,
        displayState,
      };
    });
  }, [challenges, game.challengeFrequency, userDate]);

  // D39: notify parent when today's challenge is available for card-zone navigation
  // Deps include canPlay so card zone re-enables if user logs in without page refresh
  useEffect(() => {
    if (displayItems.length > 0 && onTodayChallengeReady && !isContinuous && game.contentAvailability !== 'under_construction') {
      onTodayChallengeReady(displayItems[0].challenge);
    }
  }, [displayItems[0]?.challenge?.id, displayItems[0]?.challenge?.accessControl?.canPlay, onTodayChallengeReady, isContinuous, game.contentAvailability]);

  // Render star display
  const renderStars = (starsEarned: number, maxStars: number) => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const stars = [];
    for (let i = 0; i < maxStars; i++) {
      const filled = i < starsEarned;
      stars.push(
        <span
          key={i}
          className={cn(
            'inline-block text-[clamp(8px,2vw,12px)] md:text-[clamp(10px,1.2vw,14px)] xl:text-[clamp(10px,0.8vw,12px)] mr-0.5 leading-none',
            filled ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-500'
          )}
          style={{ WebkitTextFillColor: filled ? '#facc15' : isDark ? '#6b7280' : '#d1d5db' }}
          aria-label={filled ? 'Earned star' : 'Empty star'}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  // Render individual progress item
  const renderProgressItem = (item: ProgressDisplay, index: number) => {
    const { challenge, displayState } = item;
    const isClickable = !!onChallengeClick;

    // Use backend displayState (single source of truth)
    // Architecture violation if accessControl is missing - fail closed for security
    if (!challenge.accessControl) {
      console.warn('[Architecture Violation] Challenge missing accessControl from backend', {
        gameId: game.id,
        challengeId: challenge.id,
        challengeType: game.challengeType
      });
    }
    const backendDisplayState = challenge.accessControl?.displayState || 'locked';
    const displayStyling = getChallengeDisplayStyling(backendDisplayState, game.challengeType);
    const accessMessage = getChallengeAccessMessage(backendDisplayState, game.displayName);

    const handleClick = () => {
      if (isClickable) {
        if (backendDisplayState === 'locked') {
          // Show subscription CTA for locked challenges
          onLockedClick?.(game);
        } else if (challenge.accessControl?.canPlay && onChallengeClick) {
          onChallengeClick(challenge, item.progress);
        }
        // For display-only challenges, do nothing (non-clickable)
      }
    };
    
    const challengeContent = (
      <div
        key={`${challenge.id}-${index}`}
        className={cn(
          'flex flex-col p-[clamp(6px,0.5vw,12px)] rounded-lg border',
          'min-h-[clamp(56px,4vw,88px)]',
          // Background colors based on activity state and challenge access
          displayState.activityState === 'in_progress' ? 'bg-blue-50 dark:bg-blue-900/30' :
          (backendDisplayState === 'display-only' && game.challengeType === 'auto-challenge') ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800' :
          'bg-white dark:bg-gray-600 border-gray-200 dark:border-gray-500',
          // Display state styling from ChallengeAccessService
          backendDisplayState === 'display-only' && 'opacity-60 text-gray-500',
          // Clickability based on display styling
          isClickable && displayStyling.isClickable && 'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200',
          game.challengeFrequency === 'weekly' && 'min-h-[clamp(72px,5vw,100px)]' // Slightly taller for weekly
        )}
        // D39: always stopPropagation — containment responsibility of the tile, not conditional on clickability
        onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (isClickable && displayStyling.isClickable) handleClick(); }}
        role={isClickable && displayStyling.isClickable ? 'button' : undefined}
        tabIndex={isClickable && displayStyling.isClickable ? 0 : undefined}
        onKeyDown={isClickable && displayStyling.isClickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation(); // prevent bubbling to GameCard Enter handler
            handleClick();
          }
        } : undefined}
        title={accessMessage} // Tooltip with access message
      >
        {/* Date Label - This is the main content, centered */}
        <div className="text-[clamp(9px,0.6vw,12px)] font-medium text-gray-900 dark:text-gray-100 text-center mb-[clamp(4px,0.4vw,10px)] whitespace-pre-line">
          {item.label}
        </div>

        {/* Completion Text - Centered below date */}
        {displayState.completionText && (
          <div className="text-[clamp(9px,0.6vw,12px)] text-gray-500 dark:text-gray-400 text-center mb-[clamp(4px,0.4vw,10px)]">
            {displayState.completionText}
          </div>
        )}

        {/* Progress Display - Stars only for completed challenges */}
        {displayState.activityState === 'completed' && (
          <div className="flex items-center justify-center">
            {renderStars(displayState.starsEarned, game.numberOfStars)}
          </div>
        )}
      </div>
    );

    // Apply different rendering based on display state
    if (backendDisplayState === 'locked') {
      // Locked challenges show with lock icon and subscription CTA click
      return (
        <PremiumLock
          key={`${challenge.id}-${index}`}
          isLocked={true}
          message={accessMessage}
          onLockedClick={handleClick}
        >
          {challengeContent}
        </PremiumLock>
      );
    }
    
    // Playable and display-only challenges render without lock overlay
    return challengeContent;
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-3">
          Recent Challenges
        </div>
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className="flex flex-col p-3 bg-gray-100 dark:bg-gray-600 rounded-lg animate-pulse min-h-[80px] justify-center"
          >
            <div className="h-3 bg-gray-300 dark:bg-gray-500 rounded mb-2 w-20"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-500 rounded mb-2 w-32"></div>
            <div className="flex items-center justify-between">
              <div className="flex space-x-1">
                {[...Array(game.numberOfStars)].map((_, starIdx) => (
                  <div key={starIdx} className="w-3 h-3 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-3">
        <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-3">
          Recent Challenges
        </div>
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="text-sm text-red-800 dark:text-red-300">
            Failed to load challenges: {error.message}
          </div>
        </div>
      </div>
    );
  }

  // No challenges available
  if (!displayItems.length) {
    // Under-construction games have no challenges — let them fall through to render the tile
    // For continuous games, empty challenges is expected — continue to render play button
    if (!isContinuous && game.contentAvailability !== 'under_construction') {
      return (
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Recent Challenges
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              No challenges available yet
            </div>
          </div>
        </div>
      );
    }
  }

  // Render continuous game play button
  const renderContinuousPlayButton = () => {
    // Show error state if there's an error
    if (error) {
      return (
        <div className="space-y-3">
          <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-3">
            Game Launch Error
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="text-sm text-red-800 dark:text-red-300 mb-3">
              {error.message}
            </div>
            <button
              onClick={() => {
                setError(null);
                handleContinuousPlay();
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-3">
          Ready to Play
        </div>
        <div
          className={cn(
            'flex flex-col p-6 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500',
            'min-h-[120px] justify-center items-center',
            'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors',
            'border-sport-accent/20 hover:border-sport-accent/40'
          )}
          onClick={handleContinuousPlay}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleContinuousPlay();
            }
          }}
        >
          <div className="text-lg font-semibold text-sport-accent mb-2">
            Play Now
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 text-center">
            Start playing anytime
          </div>
        </div>
      </div>
    );
  };

  // Render enhanced today challenge card with game info
  const renderEnhancedTodayChallenge = (item: ProgressDisplay) => {
    const { challenge, displayState } = item;
    const isClickable = !!onChallengeClick;

    // Use backend displayState (single source of truth)
    // Architecture violation if accessControl is missing - fail closed for security
    if (!challenge.accessControl) {
      console.warn('[Architecture Violation] Challenge missing accessControl from backend', {
        gameId: game.id,
        challengeId: challenge.id,
        challengeType: game.challengeType
      });
    }
    const backendDisplayState = challenge.accessControl?.displayState || 'locked';
    const displayStyling = getChallengeDisplayStyling(backendDisplayState, game.challengeType);
    const accessMessage = getChallengeAccessMessage(backendDisplayState, game.displayName);

    const handleClick = () => {
      if (isClickable) {
        if (backendDisplayState === 'locked') {
          onLockedClick?.(game);
        } else if (challenge.accessControl?.canPlay && onChallengeClick) {
          onChallengeClick(challenge, item.progress);
        }
      }
    };

    const challengeContent = (
      <div
        className={cn(
          'flex flex-col p-[clamp(2px,0.6vw,6px)] md:p-[clamp(4px,1vw,8px)] xl:p-[clamp(3px,0.35vw,6px)] rounded-lg border border-gray-200 dark:border-gray-500',
          'h-[clamp(44px,12vw,64px)] md:h-[clamp(56px,8vw,80px)] xl:h-[clamp(40px,3.5vw,60px)]', // Fluid height - mobile/tablet/desktop proportions
          // D40: TODAY box bg-white is overridden by inline style sport-color tint (IS-INV-009)
          displayState.activityState === 'in_progress' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-white dark:bg-gray-600',
          // Display state styling from ChallengeAccessService
          backendDisplayState === 'display-only' && 'opacity-60 text-gray-500',
          // Clickability based on display styling
          isClickable && displayStyling.isClickable && 'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200'
        )}
        // D40: sport-color tint for visual differentiation from archive boxes (IS-INV-009 compliant)
        style={displayState.activityState !== 'in_progress' ? { backgroundColor: 'color-mix(in srgb, var(--sport-color) 12%, transparent)' } : undefined}
        // D39: always stopPropagation — containment responsibility of the tile, not conditional on clickability
        onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (isClickable && displayStyling.isClickable) handleClick(); }}
        role={isClickable && displayStyling.isClickable ? 'button' : undefined}
        tabIndex={isClickable && displayStyling.isClickable ? 0 : undefined}
        onKeyDown={isClickable && displayStyling.isClickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation(); // prevent bubbling to GameCard Enter handler
            handleClick();
          }
        } : undefined}
        title={accessMessage} // Tooltip with access message
      >
        {/* Challenge content - date at top, other content centered below */}
        <div className="flex flex-col items-center h-full">
          {/* Date Label - fixed height container for alignment across cards */}
          <div className="h-[clamp(10px,2.5vw,16px)] md:h-[clamp(14px,1.8vw,20px)] xl:h-[clamp(12px,1.1vw,18px)] flex items-center justify-center">
            <span className="text-[clamp(6px,1.6vw,9px)] md:text-[clamp(9px,1.1vw,12px)] xl:text-[clamp(8px,0.6vw,11px)] font-medium text-gray-900 dark:text-gray-100 text-center leading-none whitespace-nowrap">
              {item.label}
            </span>
          </div>

          {/* Spacer to push remaining content to center */}
          <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">

          {/* D40: Play button replaces CTA text for playable not-yet-started TODAY challenges */}
          {displayState.activityState === 'not_played' && backendDisplayState === 'playable' ? (
            <button
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleClick(); }}
              className="bg-foreground dark:bg-sport-baseball-primary text-white rounded font-oswald font-bold uppercase tracking-[0.5px] transition-all hover:-translate-y-px hover:shadow-lg px-[clamp(4px,1.2vw,10px)] py-[clamp(2px,0.5vw,5px)] text-[clamp(7px,1.8vw,11px)] md:text-[clamp(8px,0.7vw,11px)] whitespace-nowrap"
            >
              Play
            </button>
          ) : displayState.completionText && (
            <div className="text-[clamp(5px,1.4vw,8px)] md:text-[clamp(7px,0.9vw,11px)] xl:text-[clamp(7px,0.55vw,10px)] text-gray-500 dark:text-gray-400 text-center">
              {displayState.completionText}
            </div>
          )}

          {/* Progress Display - Stars only for completed challenges */}
          {displayState.activityState === 'completed' && (
            <div className="flex items-center justify-center">
              {renderStars(displayState.starsEarned, game.numberOfStars)}
            </div>
          )}
          </div>
        </div>
      </div>
    );

    // Apply different rendering based on display state
    if (backendDisplayState === 'locked') {
      // Locked challenges show with lock icon and subscription CTA click
      return (
        <PremiumLock
          isLocked={true}
          message={accessMessage}
          onLockedClick={handleClick}
        >
          {challengeContent}
        </PremiumLock>
      );
    }
    
    // Playable and display-only challenges render without lock overlay
    return challengeContent;
  };

  // Render compact challenge card for historical challenges (narrower width)
  const renderCompactChallenge = (item: ProgressDisplay, index: number) => {
    const { challenge, displayState } = item;
    const isClickable = !!onChallengeClick;

    // Use backend displayState (single source of truth)
    // Architecture violation if accessControl is missing - fail closed for security
    if (!challenge.accessControl) {
      console.warn('[Architecture Violation] Challenge missing accessControl from backend', {
        gameId: game.id,
        challengeId: challenge.id,
        challengeType: game.challengeType
      });
    }
    const backendDisplayState = challenge.accessControl?.displayState || 'locked';
    const displayStyling = getChallengeDisplayStyling(backendDisplayState, game.challengeType);
    const accessMessage = getChallengeAccessMessage(backendDisplayState, game.displayName);

    const handleClick = () => {
      if (isClickable) {
        if (backendDisplayState === 'locked') {
          onLockedClick?.(game);
        } else if (challenge.accessControl?.canPlay && onChallengeClick) {
          onChallengeClick(challenge, item.progress);
        }
      }
    };

    const challengeContent = (
      <div
        className={cn(
          'flex flex-col p-[clamp(2px,0.6vw,6px)] md:p-[clamp(4px,1vw,8px)] xl:p-[clamp(3px,0.35vw,6px)] rounded-lg border',
          'h-[clamp(44px,12vw,64px)] md:h-[clamp(56px,8vw,80px)] xl:h-[clamp(40px,3.5vw,60px)]', // Fluid height - mobile/tablet/desktop proportions
          // Background colors based on activity state and challenge access
          displayState.activityState === 'in_progress' ? 'bg-blue-50 dark:bg-blue-900/30' :
          (backendDisplayState === 'display-only' && game.challengeType === 'auto-challenge') ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800' :
          'bg-white dark:bg-gray-600 border-gray-200 dark:border-gray-500',
          // Display state styling from ChallengeAccessService
          backendDisplayState === 'display-only' && 'opacity-60 text-gray-500',
          // Clickability based on display styling
          isClickable && displayStyling.isClickable && 'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200'
        )}
        // D39: always stopPropagation — containment responsibility of the tile, not conditional on clickability
        onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (isClickable && displayStyling.isClickable) handleClick(); }}
        role={isClickable && displayStyling.isClickable ? 'button' : undefined}
        tabIndex={isClickable && displayStyling.isClickable ? 0 : undefined}
        onKeyDown={isClickable && displayStyling.isClickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation(); // prevent bubbling to GameCard Enter handler
            handleClick();
          }
        } : undefined}
        title={accessMessage} // Tooltip with access message
      >
        {/* Content wrapper - date at top, rest centered for alignment with TODAY card */}
        <div className="flex flex-col items-center h-full">
          {/* Date Label - fixed height container for alignment across cards */}
          <div className="h-[clamp(10px,2.5vw,16px)] md:h-[clamp(14px,1.8vw,20px)] xl:h-[clamp(12px,1.1vw,18px)] flex items-center justify-center">
            <span className="text-[clamp(6px,1.6vw,9px)] md:text-[clamp(9px,1.1vw,12px)] xl:text-[clamp(8px,0.6vw,11px)] font-medium text-gray-900 dark:text-gray-100 text-center leading-none whitespace-nowrap">
              {item.label}
            </span>
          </div>

          {/* Spacer to push remaining content to center */}
          <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">
            {/* Completion Text */}
            {displayState.completionText && (
              <div className="text-[clamp(5px,1.4vw,8px)] md:text-[clamp(7px,0.9vw,11px)] xl:text-[clamp(7px,0.55vw,10px)] text-gray-500 dark:text-gray-400 text-center">
                {displayState.completionText}
              </div>
            )}

            {/* Progress Display - Stars only for completed challenges */}
            {displayState.activityState === 'completed' && (
              <div className="flex items-center justify-center">
                {renderStars(displayState.starsEarned, game.numberOfStars)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
    
    // Apply different rendering based on display state
    if (backendDisplayState === 'locked') {
      // Locked challenges show with lock icon and subscription CTA click
      return (
        <PremiumLock
          key={`compact-${challenge.id}-${index}`}
          isLocked={true}
          message={accessMessage}
          onLockedClick={handleClick}
        >
          {challengeContent}
        </PremiumLock>
      );
    }
    
    // Playable and display-only challenges render without lock overlay
    return challengeContent;
  };

  // Render archive navigation card
  const renderArchiveCard = () => (
    <div
      className={cn(
        'flex flex-col p-[clamp(2px,0.6vw,8px)] md:p-[clamp(4px,1vw,12px)] xl:p-[clamp(3px,0.4vw,8px)] bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500',
        'h-[clamp(44px,12vw,64px)] md:h-[clamp(56px,8vw,80px)] xl:h-[clamp(40px,3.5vw,60px)] justify-center items-center text-center', // Fluid height - mobile/tablet/desktop
        'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200'
      )}
      // D39: stopPropagation prevents archive link click from bubbling to GameCard zone onClick
      onClick={(e) => { e.stopPropagation(); onArchivesClick?.(); }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onArchivesClick?.();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="text-[clamp(10px,2.5vw,18px)] md:text-[clamp(14px,1.8vw,20px)] xl:text-[clamp(12px,1.1vw,18px)] text-gray-400 dark:text-gray-300 mb-0.5">→</div>
      <div className="text-[clamp(5px,1.4vw,9px)] md:text-[clamp(7px,0.9vw,11px)] xl:text-[clamp(7px,0.55vw,10px)] font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
        View Archives
      </div>
    </div>
  );

  // Main component render
  return (
    <div className="space-y-3">      
      {/* Continuous Game Layout */}
      {isContinuous ? (
        renderContinuousPlayButton()
      ) : (
        /* Enhanced Challenge Display: Horizontal scroll with 7 challenges */
        (displayItems.length > 0 || game.contentAvailability === 'under_construction') && (
          <div className="space-y-3">
            {/* Mobile/Tablet: Horizontal scroll (below 1280px) - newest on left, scroll right for older */}
            <div className="block xl:hidden relative">
              {/* Wrapper with scroll indicators outside the stream */}
              <div className="flex items-stretch">
                {/* Left scroll indicator triangle - shows when scrolled away from start */}
                {showLeftIndicator && (
                  <div className="flex-shrink-0 flex items-center pr-0.5 pb-2">
                    <span
                      className="text-gray-400 text-[14px] leading-none"
                      style={{ transform: 'scaleY(1.8) scaleX(0.7)' }}
                    >◀</span>
                  </div>
                )}
                {/* Scroll container */}
                <div
                  ref={scrollContainerRef}
                  className="flex-1 flex gap-[clamp(3px,0.8vw,6px)] overflow-x-auto pb-2 scrollbar-hide"
                >
                  {/* Enhanced today challenge - wider (or under-construction tile) */}
                  {/* IMA-431: Under-construction tile replaces today slot; always renders even if no challenges */}
                  {game.contentAvailability === 'under_construction'
                    ? (
                      <div className="flex-shrink-0 w-[clamp(80px,22vw,120px)] md:w-[clamp(100px,14vw,144px)]">
                        <UnderConstructionTile game={game} />
                      </div>
                    ) : displayItems[0] && (
                      <div className="flex-shrink-0 w-[clamp(80px,22vw,120px)] md:w-[clamp(100px,14vw,144px)]">
                        {renderEnhancedTodayChallenge(displayItems[0])}
                      </div>
                    )}
                  {/* Historical challenges - compact squares */}
                  {displayItems.slice(1).map((item, index) => (
                    <div key={`mobile-${item.challenge.id}-${index + 1}`} className="flex-shrink-0 w-[clamp(48px,13vw,72px)] md:w-[clamp(64px,9vw,96px)]">
                      {renderCompactChallenge(item, index + 1)}
                    </div>
                  ))}
                  {/* Archive navigation */}
                  {onArchivesClick && (
                    <div className="flex-shrink-0 w-[clamp(48px,13vw,72px)] md:w-[clamp(64px,9vw,96px)]">
                      {renderArchiveCard()}
                    </div>
                  )}
                </div>
                {/* Right scroll indicator triangle - shows when more content to the right */}
                {/* Uses negative margin to extend into GameCard padding for better centering */}
                {showRightIndicator && (
                  <div className="flex-shrink-0 flex items-center pl-0.5 pb-2 -mr-1.5 md:-mr-2">
                    <span
                      className="text-gray-400 text-[14px] leading-none"
                      style={{ transform: 'scaleY(1.8) scaleX(0.7)' }}
                    >▶</span>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: Flexible layout - all 7 days + archives visible without scrolling (1280px+) */}
            <div className="hidden xl:block">
              <div className="flex gap-[clamp(6px,0.4vw,12px)]">
                {/* Enhanced today challenge - takes more space (or under-construction tile) */}
                {/* IMA-431: Under-construction tile replaces today slot; always renders even if no challenges */}
                {game.contentAvailability === 'under_construction'
                  ? (
                    <div className="flex-[2] min-w-0">
                      <UnderConstructionTile game={game} />
                    </div>
                  ) : displayItems[0] && (
                    <div className="flex-[2] min-w-0">
                      {renderEnhancedTodayChallenge(displayItems[0])}
                    </div>
                  )}

                {/* Historical challenges (6 days) - equal flexible width */}
                {displayItems.slice(1).map((item, index) => (
                  <div key={`compact-${item.challenge.id}-${index + 1}`} className="flex-1 min-w-0">
                    {renderCompactChallenge(item, index + 1)}
                  </div>
                ))}

                {/* Archive navigation */}
                {onArchivesClick && (
                  <div className="flex-1 min-w-0">
                    {renderArchiveCard()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}